import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import Navbar from "./Navbar";
import LineupModal from "./LineupModal";
import HelpTooltip from "./HelpTooltip";
import { getTeamTheme } from "./teamTheme";
import "./variables.css";
import "./TeamList.css";
import "./PlayGame.css";
import API_URL from "./config";

const hexToRgba = (hex, alpha) => {
  if (!hex || typeof hex !== "string" || hex.length < 7) {
    return `rgba(18, 82, 210, ${alpha})`;
  }
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

const ovrTier = (rating) => {
  if (rating >= 88) return "elite";
  if (rating >= 80) return "starter";
  if (rating >= 72) return "solid";
  if (rating >= 65) return "average";
  return "below";
};

const POS_CLASSES = ["PG", "SG", "SF", "PF", "C"];
const posClass = (pos) => (POS_CLASSES.includes(pos) ? pos : "unknown");

const teamInitials = (name) => {
  if (!name) return "?";
  const words = name.trim().split(/\s+/);
  if (words.length === 1) return words[0].slice(0, 2).toUpperCase();
  return (words[words.length - 1][0] || "").toUpperCase();
};

const playerAge = (birthdate) => {
  if (!birthdate) return null;
  const parts = String(birthdate).split("/");
  if (parts.length !== 3) return null;
  const yr = parseInt(parts[2], 10);
  if (!yr) return null;
  const age = new Date().getFullYear() - yr;
  return age > 0 && age < 60 ? age : null;
};

const deriveRoleTag = (p) => {
  const three = p.three || 0;
  const close = p.close || 0;
  const dunk = p.dunk || 0;
  const passing = p.passing || 0;
  const dribbling = p.dribbling || 0;
  const oiq = p.offensive_iq || 0;
  const diq = p.defensive_iq || 0;
  const reb = p.rebounding || 0;
  const strength = p.strength || 0;
  const jumping = p.jumping || 0;
  const speed = p.speed || 0;
  const pos = p.position;

  if (three >= 78 && diq >= 80) return { label: "3-and-D", tone: "twoway" };
  if ((pos === "C" || pos === "PF") && (diq + jumping + strength) / 3 >= 82) return { label: "Rim Protector", tone: "defense" };
  if ((pos === "C" || pos === "PF") && dunk >= 85 && close >= 78) return { label: "Paint Finisher", tone: "offense" };
  if (reb >= 85) return { label: "Glass Cleaner", tone: "defense" };
  if (three >= 85) return { label: "Sharpshooter", tone: "offense" };
  if (three >= 75 && three > close + 3) return { label: "Deep Threat", tone: "offense" };
  if ((pos === "PG" || pos === "SG") && (passing + dribbling + oiq) / 3 >= 82) return { label: "Playmaker", tone: "offense" };
  if (pos === "PG" && passing + oiq >= 160) return { label: "Floor General", tone: "offense" };
  if (close >= 80 && speed >= 78) return { label: "Slasher", tone: "offense" };
  if (diq >= 85) return { label: "Lockdown", tone: "defense" };
  if (speed >= 85 && jumping >= 82) return { label: "Athlete", tone: "neutral" };

  // Play-style fallbacks by position — never "Reserve"
  if (pos === "PG") {
    return passing >= three
      ? { label: "Pass-First PG", tone: "offense" }
      : { label: "Combo Guard", tone: "neutral" };
  }
  if (pos === "SG") {
    return three >= close
      ? { label: "Wing Scorer", tone: "offense" }
      : { label: "Combo Guard", tone: "neutral" };
  }
  if (pos === "SF") {
    return diq >= 75
      ? { label: "Two-Way Wing", tone: "twoway" }
      : { label: "Wing", tone: "neutral" };
  }
  if (pos === "PF") {
    return three >= 70
      ? { label: "Stretch Big", tone: "offense" }
      : { label: "Combo Forward", tone: "neutral" };
  }
  if (pos === "C") {
    return three >= 70
      ? { label: "Stretch Big", tone: "offense" }
      : { label: "Traditional Big", tone: "neutral" };
  }
  return { label: "Versatile", tone: "neutral" };
};

const PlayGame = () => {
  const [save, setSave] = useState(null);
  const [team, setTeam] = useState(null);
  const [roster, setRoster] = useState([]);
  const [game, setGame] = useState(null);
  const [startingLineup, setStartingLineup] = useState([]);
  const [bench, setBench] = useState([]);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [step, setStep] = useState("lineup"); // lineup, sim, result
  const [hasPreset, setHasPreset] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const TOTAL_MINUTES = 240;
  const DEFAULT_STARTER_MINUTES = 34;
  const DEFAULT_BENCH_MINUTES = 14;

  const [homeScore, setHomeScore] = useState(0);
  const [awayScore, setAwayScore] = useState(0);
  const [minutesByPlayerId, setMinutesByPlayerId] = useState({});
  const [opponentTeam, setOpponentTeam] = useState(null);
  const [opponentLineup, setOpponentLineup] = useState({ starters: [], bench: [] });
  const [playerStats, setPlayerStats] = useState({});
  
  // Warning modal state for preceding unplayed games
  const [showWarningModal, setShowWarningModal] = useState(false);
  const [precedingGames, setPrecedingGames] = useState([]);
  const [showLineupModal, setShowLineupModal] = useState(false);

  const formatHeight = (h) => {
    if (h === null || h === undefined) return "N/A";
    const n = Number(h);
    if (Number.isNaN(n)) return h;
    const feet = Math.floor(n / 12);
    const inches = n % 12;
    return `${feet}\'${inches}"`;
  };

  const formatWeight = (w) => {
    if (!w) return "N/A";
    return `${w} lbs`;
  };

  const avg = (values) => {
    const nums = values.filter((v) => typeof v === "number" && !Number.isNaN(v));
    return nums.length ? nums.reduce((sum, v) => sum + v, 0) / nums.length : 0;
  };

  const playerOverall = (player) => {
    const offense = avg([
      player.close,
      player.mid,
      player.three,
      player.free_throw,
      player.dunk,
      player.passing,
      player.dribbling,
      player.offensive_iq,
    ]);

    const defense = avg([
      player.defensive_iq,
      player.rebounding,
      player.strength,
      player.jumping,
    ]);

    const athletic = avg([
      player.speed,
      player.stamina,
      player.injury,
      player.jumping,
    ]);

    return offense * 0.45 + defense * 0.35 + athletic * 0.2;
  };

  const formatRating = (player) => Math.round(playerOverall(player));

  const buildAutoLineup = (fullRoster) => {
    const rated = fullRoster.map((player) => ({
      player,
      rating: playerOverall(player),
    }));
    const usedIds = new Set();

    const pickBestByPosition = (position) => {
      const candidates = rated
        .filter((item) => item.player.position === position && !usedIds.has(item.player._id))
        .sort((a, b) => b.rating - a.rating);
      return candidates.length ? candidates[0].player : null;
    };

    const starters = [];
    ["PG", "SG", "SF", "PF", "C"].forEach((position) => {
      const pick = pickBestByPosition(position);
      if (pick) {
        starters.push(pick);
        usedIds.add(pick._id);
      }
    });

    if (starters.length < 5) {
      const remaining = rated
        .filter((item) => !usedIds.has(item.player._id))
        .sort((a, b) => b.rating - a.rating);
      for (const item of remaining) {
        if (starters.length >= 5) break;
        starters.push(item.player);
        usedIds.add(item.player._id);
      }
    }

    const bench = rated
      .filter((item) => !usedIds.has(item.player._id))
      .sort((a, b) => b.rating - a.rating)
      .slice(0, 5)
      .map((item) => item.player);

    return { starters, bench };
  };

  useEffect(() => {
    const loadGameData = async () => {
      try {
        setLoading(true);
        const activeSaveId = localStorage.getItem("activeSaveId");
        const user = JSON.parse(localStorage.getItem("user") || "{}");
        const userId = user._id;

        if (!activeSaveId) {
          setError("No active save file. Please select or create a save.");
          setLoading(false);
          return;
        }

        if (!userId) {
          setError("Please log in to play games");
          setLoading(false);
          return;
        }

        // If an in-progress live game exists for this save, resume it.
        try {
          const activeRes = await fetch(
            `${API_URL}/api/saves/${activeSaveId}/active-live-game`,
            { headers: { "X-User-ID": userId } }
          );
          if (activeRes.ok) {
            const activeData = await activeRes.json();
            if (activeData.active) {
              navigate("/live-game", { state: { resume: true } });
              return;
            }
          }
        } catch (err) {
          // Non-fatal — fall through to normal flow
        }

        // Fetch save details
        const saveRes = await fetch(`${API_URL}/api/saves/${activeSaveId}`, {
          headers: {
            "X-User-ID": userId,
          },
        });

        if (!saveRes.ok) {
          const data = await saveRes.json();
          setError(data.detail || "Failed to load save file");
          setLoading(false);
          return;
        }

        const saveData = await saveRes.json();
        setSave(saveData.save);

        const teamId = saveData.save.teamId;
        if (!teamId) {
          setError("No team associated with this save");
          setLoading(false);
          return;
        }

        // Fetch team roster
        const teamRes = await fetch(`${API_URL}/api/teams/${teamId}/roster?save_id=${activeSaveId}`);
        if (!teamRes.ok) {
          const data = await teamRes.json();
          setError(data.detail || "Failed to load team");
          setLoading(false);
          return;
        }

        const teamData = await teamRes.json();
        setTeam(teamData.team);
        setRoster(teamData.roster || []);

        // Load team's preset lineup
        const teamWithLineup = await fetch(`${API_URL}/api/teams/${teamId}?save_id=${activeSaveId}`, {
          headers: { "X-User-ID": user._id },
        });

        if (teamWithLineup.ok) {
          const teamLineupData = await teamWithLineup.json();
          const team = teamLineupData.team || {};
          const lineupData = team.current_lineup;

          if (lineupData && lineupData.on_field && lineupData.bench && teamData.roster) {
            // Map lineup IDs back to full player objects
            const onFieldPlayers = lineupData.on_field
              .map(id => teamData.roster.find(p => p._id === id))
              .filter(p => p);
            const benchPlayers = lineupData.bench
              .map(id => teamData.roster.find(p => p._id === id))
              .filter(p => p);

            if (onFieldPlayers.length === 5 && benchPlayers.length === 5) {
              setStartingLineup(onFieldPlayers);
              setBench(benchPlayers);
              setHasPreset(true);

              // Load minutes from lineup data, or use defaults
              const minutes = {};
              onFieldPlayers.forEach(p => {
                minutes[p._id] = lineupData.minutesByPlayerId?.[p._id] ?? DEFAULT_STARTER_MINUTES;
              });
              benchPlayers.forEach(p => {
                minutes[p._id] = lineupData.minutesByPlayerId?.[p._id] ?? DEFAULT_BENCH_MINUTES;
              });
              setMinutesByPlayerId(minutes);
            }
          }
        }

        // Get game from location state (passed from schedule)
        const gameData = location.state?.game;
        if (gameData) {
          setGame(gameData);
        } else {
          setError("No game selected");
        }

        if (gameData) {
          const opponentId = gameData.homeTeam === teamId ? gameData.awayTeam : gameData.homeTeam;
          const opponentRes = await fetch(`${API_URL}/api/teams/${opponentId}/roster?save_id=${activeSaveId}`);
          if (opponentRes.ok) {
            const opponentData = await opponentRes.json();
            setOpponentTeam(opponentData.team || null);
            setOpponentLineup(buildAutoLineup(opponentData.roster || []));
          }
        }

        setLoading(false);
      } catch (err) {
        setError(err.message || "Server error");
        setLoading(false);
      }
    };

    loadGameData();
  }, [location]);

  // Fetch season stats for all visible players (user roster + opponent starters/bench)
  useEffect(() => {
    if (!save) return;
    const ids = new Set();
    roster.forEach((p) => p?._id && ids.add(p._id));
    (opponentLineup.starters || []).forEach((p) => p?._id && ids.add(p._id));
    (opponentLineup.bench || []).forEach((p) => p?._id && ids.add(p._id));
    if (ids.size === 0) return;

    const activeSaveId = localStorage.getItem("activeSaveId");
    const season = save.currentSeason || 2025;
    const url = `${API_URL}/api/stats/players/season-summary-batch?save_id=${activeSaveId}&season=${season}&player_ids=${Array.from(ids).join(",")}`;

    fetch(url)
      .then((r) => (r.ok ? r.json() : { stats: {} }))
      .then((data) => setPlayerStats(data.stats || {}))
      .catch(() => setPlayerStats({}));
  }, [save, roster, opponentLineup]);

  // Check for preceding unplayed games when game is loaded
  useEffect(() => {
    if (!game || !save) return;

    const checkPrecedingGames = async () => {
      try {
        const activeSaveId = localStorage.getItem("activeSaveId");
        const user = JSON.parse(localStorage.getItem("user") || "{}");
        const userId = user._id;

        const res = await fetch(
          `${API_URL}/api/saves/${activeSaveId}/games/${game._id}/preceding-unplayed`,
          { headers: { "X-User-ID": userId } }
        );

        if (res.ok) {
          const data = await res.json();
          if (data.willSimulate) {
            setPrecedingGames(data.precedingGames);
            setShowWarningModal(true);
          }
        }
      } catch (err) {
        // Error checking preceding games
      }
    };

    checkPrecedingGames();
  }, [game, save]);

  const removeFromBench = (playerId) => {
    setBench(bench.filter(p => p._id !== playerId));
    setMinutesByPlayerId((prev) => {
      const next = { ...prev };
      delete next[playerId];
      return next;
    });
  };

  const isInStarting = (playerId) => {
    return startingLineup.some(p => p._id === playerId);
  };

  const isInBench = (playerId) => {
    return bench.some(p => p._id === playerId);
  };

  const getMinutesForPlayer = (playerId) => minutesByPlayerId[playerId] ?? 0;

  const reloadLineupData = async () => {
    try {
      const activeSaveId = localStorage.getItem("activeSaveId");
      const user = JSON.parse(localStorage.getItem("user") || "{}");
      
      if (!activeSaveId || !team?._id) return;

      const teamWithLineup = await fetch(`${API_URL}/api/teams/${team._id}?save_id=${activeSaveId}`, {
        headers: { "X-User-ID": user._id },
      });

      if (teamWithLineup.ok) {
        const teamLineupData = await teamWithLineup.json();
        const lineupData = teamLineupData.team?.current_lineup;

        if (lineupData && lineupData.on_field && lineupData.bench) {
          // Load minutes from lineup data, or use defaults
          const minutes = {};
          lineupData.on_field.forEach((playerId) => {
            minutes[playerId] = lineupData.minutesByPlayerId?.[playerId] ?? DEFAULT_STARTER_MINUTES;
          });
          lineupData.bench.forEach((playerId) => {
            minutes[playerId] = lineupData.minutesByPlayerId?.[playerId] ?? DEFAULT_BENCH_MINUTES;
          });
          setMinutesByPlayerId(minutes);
        }
      }
    } catch (err) {
      // Failed to reload lineup data
    }
  };

  const handleSimulate = () => {
    const isHome = game.homeTeam === team._id;
    const lineup = [...startingLineup, ...bench].map((player) => ({
      playerId: player._id,
      minutes: minutesByPlayerId[player._id] || 0,
    }));

    const homeStarterIds = isHome ? startingLineup.map(p => p._id) : opponentLineup.starters.map(p => p._id);
    const awayStarterIds = isHome ? opponentLineup.starters.map(p => p._id) : startingLineup.map(p => p._id);

    // Navigate to the live game experience
    navigate("/live-game", {
      state: {
        gameId: game._id,
        homeTeamId: game.homeTeam,
        awayTeamId: game.awayTeam,
        userTeamId: team._id,
        homeTeamName: game.homeTeamName,
        awayTeamName: game.awayTeamName,
        season: save?.currentSeason || 2025,
        saveId: localStorage.getItem("activeSaveId") || undefined,
        homeLineup: isHome ? lineup : null,
        awayLineup: isHome ? null : lineup,
        homeStarterIds,
        awayStarterIds,
        gameType: game.gameType || "regular",
      },
    });
  };

  const handleSaveResult = async () => {
    try {
      const user = JSON.parse(localStorage.getItem("user") || "{}");
      const userId = user._id;
      const activeSaveId = localStorage.getItem("activeSaveId");

      const gameResult = {
        gameId: game._id,
        season: save.currentSeason || 2025,
        homeTeam: game.homeTeam,
        awayTeam: game.awayTeam,
        homeScore: homeScore,
        awayScore: awayScore,
        playerStats: [] // Will be populated with sim logic later
      };

      const res = await fetch(`${API_URL}/api/saves/${activeSaveId}/games`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-User-ID": userId,
        },
        body: JSON.stringify(gameResult),
      });

      if (!res.ok) {
        const data = await res.json();
        alert(data.detail || "Failed to save game result");
        return;
      }

      navigate("/my-team");
    } catch (err) {
      alert(err.message || "Failed to save game result");
    }
  };

  // ── Computed values for hero & strength bar ──────────────────────
  const isUserHome = team && game && game.homeTeam === team._id;
  const userTeamName = team?.name || (isUserHome ? game?.homeTeamName : game?.awayTeamName) || "Your Team";
  const opponentName = opponentTeam?.name || (isUserHome ? game?.awayTeamName : game?.homeTeamName) || "Opponent";

  const userTheme = getTeamTheme(team || userTeamName);
  const oppTheme = getTeamTheme(opponentTeam || opponentName);

  const userStartersAvg =
    startingLineup.length > 0
      ? Math.round(startingLineup.reduce((sum, p) => sum + playerOverall(p), 0) / startingLineup.length)
      : 0;
  const oppStartersAvg =
    opponentLineup.starters.length > 0
      ? Math.round(opponentLineup.starters.reduce((sum, p) => sum + playerOverall(p), 0) / opponentLineup.starters.length)
      : 0;
  const maxAvg = Math.max(userStartersAvg, oppStartersAvg, 1);

  const matchupStyle = {
    "--pg-user-primary": hexToRgba(userTheme.primary, 0.35),
    "--pg-opp-primary": hexToRgba(oppTheme.primary, 0.35),
  };

  const renderPlayerRow = (player, isBench, showMinutes = true) => {
    const rating = formatRating(player);
    const tier = ovrTier(rating);
    const injured = player.injuryType && player.injuryGamesRemaining > 0;
    const role = deriveRoleTag(player);
    const age = playerAge(player.birthdate);
    const jersey = player.number;
    const stats = playerStats[player._id];
    const hasStats = stats && stats.gp > 0;
    return (
      <div key={player._id} className={`pg-player-row${isBench ? " bench" : ""}`}>
        <div className={`pg-pos ${posClass(player.position)}`}>{player.position || "?"}</div>
        <div className="pg-player-main">
          <div className="pg-player-top">
            <span className="pg-player-name">
              {jersey != null && <span className="pg-jersey">#{jersey}</span>}
              {player.displayName || player.name}
              {injured && (
                <span
                  className="pg-injury-inline"
                  title="This player is injured. It is advised to let them recover before putting them in a game."
                >
                  !
                </span>
              )}
            </span>
            <span className={`pg-role ${role.tone}`}>{role.label}</span>
          </div>
          <div className="pg-player-sub">
            {age != null && <span className="pg-age">{age}yo</span>}
            {hasStats ? (
              <span className="pg-mini-stats">
                <span>{stats.ppg}<em>ppg</em></span>
                <span>{stats.rpg}<em>rpg</em></span>
                <span>{stats.apg}<em>apg</em></span>
              </span>
            ) : (
              <span className="pg-mini-stats muted">No games this season</span>
            )}
          </div>
        </div>
        <div className={`pg-ovr ${tier}`}>{rating}</div>
        {showMinutes && (
          <div className="pg-min">{getMinutesForPlayer(player._id)}m</div>
        )}
      </div>
    );
  };

  return (
    <>
      <Navbar />

      {showWarningModal && (
        <div className="pg-modal-backdrop" onClick={() => setShowWarningModal(false)}>
          <div className="pg-modal" onClick={(e) => e.stopPropagation()}>
            <h2 className="pg-modal-title">⚠️ Unplayed Games</h2>
            <p className="pg-modal-body">
              Playing this game will simulate the following unplayed games up to this point:
            </p>

            {precedingGames.length > 0 && (
              <div className="pg-modal-list">
                {precedingGames.map((g, idx) => (
                  <div key={idx} className="pg-modal-game">
                    <div className="pg-modal-game-teams">
                      {g.awayTeamName} @ {g.homeTeamName}
                    </div>
                    <div className="pg-modal-game-date">
                      {new Date(g.date).toLocaleDateString("en-US", {
                        month: "short",
                        day: "numeric",
                        year: "numeric",
                      })}
                    </div>
                  </div>
                ))}
              </div>
            )}

            <div className="pg-modal-actions">
              <button className="pg-modal-btn secondary" onClick={() => setShowWarningModal(false)}>
                Cancel
              </button>
              <button className="pg-modal-btn primary" onClick={() => setShowWarningModal(false)}>
                Continue
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="pg-page">
        <div className="pg-content">
          {/* Top bar with title + actions */}
          <div className="pg-topbar">
            <div className="pg-topbar-title">
              <h1 className="pg-title">Play Game</h1>
              {save && <span className="pg-subtitle">Season {save.currentSeason || 2025}</span>}
            </div>
          </div>

          {error && (
            <div className="pg-error">
              <span>⚠</span>
              <span>{error}</span>
            </div>
          )}

          {loading && (
            <div className="pg-loading">
              <div className="pg-spinner" />
              <p>Loading game data...</p>
            </div>
          )}

          {!loading && game && team && step === "lineup" && (
            <>
              {/* Slim matchup strip — user always on left, opponent on right */}
              <div className="pg-matchup" style={matchupStyle}>
                <div className="pg-matchup-side user">
                  <div
                    className="pg-matchup-badge"
                    style={{ background: `linear-gradient(135deg, ${userTheme.primary}, ${userTheme.secondary})` }}
                  >
                    {teamInitials(userTeamName)}
                  </div>
                  <div className="pg-matchup-info">
                    <div className="pg-matchup-loc">{isUserHome ? "HOME" : "AWAY"}</div>
                    <div className="pg-matchup-name">{userTeamName}</div>
                    <div className="pg-matchup-ovr">
                      OVERALL<strong>{userStartersAvg}</strong>
                    </div>
                  </div>
                </div>

                <div className="pg-matchup-center">
                  <div className="pg-matchup-vs">VS</div>
                </div>

                <div className="pg-matchup-side opp">
                  <div
                    className="pg-matchup-badge"
                    style={{ background: `linear-gradient(135deg, ${oppTheme.primary}, ${oppTheme.secondary})` }}
                  >
                    {teamInitials(opponentName)}
                  </div>
                  <div className="pg-matchup-info">
                    <div className="pg-matchup-loc">{isUserHome ? "AWAY" : "HOME"}</div>
                    <div className="pg-matchup-name">{opponentName}</div>
                    <div className="pg-matchup-ovr">
                      OVERALL<strong>{oppStartersAvg}</strong>
                    </div>
                  </div>
                </div>
              </div>

              {!hasPreset ? (
                <div className="pg-no-preset">
                  <div className="pg-no-preset-icon">🏀</div>
                  <h3 className="pg-no-preset-title">No Lineup Preset Created</h3>
                  <p className="pg-no-preset-msg">
                    You must create a lineup preset before you can play games. Head to My Team and set up your starting five.
                  </p>
                  <button
                    className="pg-no-preset-btn"
                    onClick={() => navigate("/my-team", { state: { openLineupModal: true } })}
                  >
                    Go to My Team & Create Preset
                  </button>
                </div>
              ) : (
                <div className="pg-rosters">
                  {/* Your roster: starters + bench */}
                  <div className="pg-roster-card">
                    <div className="pg-roster-head">
                      <h3 className="pg-roster-title">
                        <span className="pg-roster-accent user" />
                        Your Team
                        <HelpTooltip
                          text="Score is calculated based on player overalls, team lineup, and boosts from practice. Ensure you have a balanced lineup with good players to get a good score."
                          position="right"
                        />
                      </h3>
                      <span className="pg-roster-meta">OVERALL<strong>{userStartersAvg}</strong></span>
                    </div>
                    {startingLineup.map((p) => renderPlayerRow(p, false))}
                    {bench.length > 0 && (
                      <>
                        <div className="pg-divider">Bench · {bench.length}</div>
                        {bench.map((p) => renderPlayerRow(p, true))}
                      </>
                    )}
                  </div>

                  {/* Opponent starters */}
                  <div className="pg-roster-card">
                    <div className="pg-roster-head">
                      <h3 className="pg-roster-title">
                        <span className="pg-roster-accent opp" />
                        {opponentName}
                      </h3>
                      <span className="pg-roster-meta">OVERALL<strong>{oppStartersAvg}</strong></span>
                    </div>
                    {opponentLineup.starters.length === 0 ? (
                      <p style={{ color: "var(--color-text-secondary)", fontSize: 12 }}>
                        Opponent lineup unavailable.
                      </p>
                    ) : (
                      <>
                        {opponentLineup.starters.map((p) => renderPlayerRow(p, false, false))}
                        {opponentLineup.bench && opponentLineup.bench.length > 0 && (
                          <>
                            <div className="pg-divider">Bench · {opponentLineup.bench.length}</div>
                            {opponentLineup.bench.map((p) => renderPlayerRow(p, true, false))}
                          </>
                        )}
                      </>
                    )}
                  </div>
                </div>
              )}

              {hasPreset && (
                <div className="pg-bottom-actions">
                  <button className="pg-btn edit" onClick={() => setShowLineupModal(true)}>
                    Edit Lineup
                  </button>
                  <button className="pg-btn simulate" onClick={handleSimulate}>
                    ▶ Simulate Game
                  </button>
                </div>
              )}
            </>
          )}
        </div>

        {showLineupModal && (
          <LineupModal
            roster={roster}
            teamId={team?._id}
            onClose={() => {
              setShowLineupModal(false);
              reloadLineupData();
            }}
          />
        )}
      </div>
    </>
  );
};

export default PlayGame;
